import { Input } from '@atlas/ui-primitives'
import { useEffect, useState } from 'react'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { VariableControl } from '@/components/prompts/PromptPreviewForm'
import type { PromptListRecord, PromptVariable } from '@/domain/prompt.types'
import { isBlankVariableValue, withoutBlankValues } from '@/domain/prompt.types'

export interface AddTestCaseDialogProps {
	open: boolean
	variables: PromptVariable[]
	/** Items for `list`-typed variables, keyed by list code. */
	lists: PromptListRecord[]
	submitting?: boolean
	errorMessage?: string | null
	onCreate: (input: { name: string; values: Record<string, unknown> }) => void
	onCancel: () => void
}

/**
 * Add a test case by hand.
 *
 * Drafting one with GenAI is asynchronous and needs a working model credential, so
 * it cannot be the only way in: a prompt with no cases cannot be evaluated at all,
 * and the publish gate reads eval results. This is the path that always works.
 *
 * The form is generated from the prompt's variable schema rather than being a JSON
 * editor, reusing the preview form's `VariableControl` — a case supplies exactly
 * the payload a preview does, so the same control is correct for both and a typed
 * value cannot be mistyped into a string.
 *
 * Assertions are deliberately not editable here. A case with none still asserts
 * something real — that the prompt renders and executes without erroring — and an
 * assertion builder is its own surface rather than a field on this dialog.
 */
export function AddTestCaseDialog({
	open,
	variables,
	lists,
	submitting,
	errorMessage,
	onCreate,
	onCancel,
}: AddTestCaseDialogProps) {
	const [name, setName] = useState('')
	const [values, setValues] = useState<Record<string, unknown>>({})

	useEffect(() => {
		if (!open) return
		setName('')
		setValues({})
	}, [open])

	// The SAME emptiness test the payload builder uses. Checking only `undefined` here
	// let a cleared field read as filled, so Save stayed enabled on a form whose
	// required value was then stripped from the payload.
	const missing = variables.filter((v) => v.required && isBlankVariableValue(values[v.key]))
	const canSubmit = name.trim() !== '' && missing.length === 0

	return (
		<ConfirmDialog
			open={open}
			title="Add a test case"
			description="A named set of variable values. Running the evals renders the prompt with each case."
			confirmLabel="Add case"
			busyLabel="Adding…"
			submitting={submitting}
			confirmDisabled={!canSubmit}
			errorMessage={errorMessage}
			onConfirm={() => {
				// Empty optionals are omitted rather than sent as '': the render engine
				// reads an absent value as "use the default", which is what blank means.
				onCreate({ name: name.trim(), values: withoutBlankValues(values) })
			}}
			onCancel={onCancel}
		>
			<div className="space-y-3">
				<label className="block">
					<span className="mb-1 block text-ds-2xs text-ds-text-subtle">Case name</span>
					<Input
						value={name}
						aria-label="Case name"
						placeholder="Blocking rule, Spanish"
						onChange={(e) => setName(e.target.value)}
					/>
				</label>

				{variables.length === 0 ? (
					<p className="text-ds-sm text-ds-text-muted">
						This prompt takes no variables, so a case only needs a name — it checks that the prompt renders and
						executes.
					</p>
				) : (
					variables.map((variable) => (
						<VariableControl
							key={variable.key}
							variable={variable}
							value={values[variable.key]}
							items={lists.find((l) => l.code === variable.listCode)?.items ?? []}
							onChange={(v) => setValues((prev) => ({ ...prev, [variable.key]: v }))}
						/>
					))
				)}

				{missing.length > 0 ? (
					<p className="text-ds-2xs text-ds-text-subtle">
						Required and still empty: {missing.map((v) => v.label || v.key).join(', ')}
					</p>
				) : null}
			</div>
		</ConfirmDialog>
	)
}
